import { useEffect, useMemo, useState, type JSX } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  ZOHO_DCS,
  ZOHO_MODULES,
  type ZohoDc,
  type ZohoImportRun,
  type ZohoModule,
  type ZohoSettings,
  type ZohoSettingsPatch,
  type ZohoStatusResponse,
} from '@profiler/shared'
import { useApi } from '../auth'
import { EM_DASH, formatDateTime, formatNumber } from '../format'
import { qk, useZohoSpam, useZohoStatus } from '../queries'
import { ErrorPanel, LoadingPanel } from './States'

/**
 * The Zoho CRM block of Settings.
 *
 * Everything the worker stamps is shown as it is stamped — org, integration
 * user, channel, last push and pull, the last error — and nothing here is
 * assumed from the browser. Connect asks the worker to run the handshake and
 * then watches the block until it answers; the same for an import. The few
 * fields an administrator sets travel on their own route and merge over the
 * stored block, so a stale tab cannot blank what the worker wrote.
 */

export type ConnectionState =
  | 'not_deployed'
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'paused'
  | 'error'

const CONNECT_WAIT_MS = 90_000
const DC_LABEL: Record<ZohoDc, string> = {
  in: 'India (zoho.in)',
  com: 'United States (zoho.com)',
  eu: 'Europe (zoho.eu)',
  au: 'Australia (zoho.com.au)',
  jp: 'Japan (zoho.jp)',
  ca: 'Canada (zohocloud.ca)',
  sa: 'Saudi Arabia (zoho.sa)',
}

/** What the chip says, from the block and from whether a connect was just asked for. */
/**
 * How full the progress bar is.
 *
 * A live run is clamped inside 5-95%: never zero, because a run that has
 * started should look started, and never full, because it has not finished. A
 * finished run is 1, and anything else has no bar at all.
 */
function progressFraction(live: boolean, phaseIndex: number, status: string): number | null {
  if (live) return Math.min(0.95, Math.max(0.05, (phaseIndex + 0.5) / 6))
  return status === 'done' ? 1 : null
}

export function connectionState(
  status: ZohoStatusResponse | undefined,
  requestedAt: number | null,
  now = Date.now(),
): ConnectionState {
  if (!status) return 'disconnected'
  if (!status.deployed) return 'not_deployed'
  const z = status.settings
  const errorAt = z.lastErrorAt ? Date.parse(z.lastErrorAt) : null
  const connectedAt = z.connectedAt ? Date.parse(z.connectedAt) : null
  const waiting = requestedAt !== null && now - requestedAt < CONNECT_WAIT_MS
  if (
    waiting &&
    (connectedAt === null || connectedAt < requestedAt) &&
    (errorAt === null || errorAt < requestedAt)
  )
    return 'connecting'
  if (errorAt !== null && (connectedAt === null || errorAt > connectedAt)) return 'error'
  if (!status.connected) return 'disconnected'
  return z.paused ? 'paused' : 'connected'
}

/** The required fields the handshake did not find, grouped the way Setup → Modules is. */
export function missingFieldLines(missing: readonly string[]): Array<{ module: string; fields: string[] }> {
  const byModule = new Map<string, string[]>()
  for (const entry of missing) {
    const dot = entry.indexOf('.')
    const module = dot > 0 ? entry.slice(0, dot) : 'Other'
    const field = dot > 0 ? entry.slice(dot + 1) : entry
    byModule.set(module, [...(byModule.get(module) ?? []), field])
  }
  return [...byModule.entries()].map(([module, fields]) => ({ module, fields }))
}

/** One line for where an import is, and how far along. */
export function importSummary(run: ZohoImportRun | null): {
  label: string
  live: boolean
  fraction: number | null
} {
  if (!run) return { label: 'No import has run yet.', live: false, fraction: null }
  const live = run.status === 'queued' || run.status === 'running'
  const records = ZOHO_MODULES.reduce((n, m) => n + (run.progress[m]?.records ?? 0), 0)
  const phaseIndex = ['accounts', 'contacts', 'leads', 'resolve', 'resolve_by_name', 'finish'].indexOf(
    run.phase,
  )
  const fraction = progressFraction(live, phaseIndex, run.status)
  const mode = run.mode === 'dry_run' ? 'Dry run' : 'Import'
  if (run.status === 'failed') {
    const why = run.error ? `: ${run.error}` : ''
    return { label: `${mode} failed${why}`, live: false, fraction }
  }
  if (run.status === 'done')
    return {
      label: `${mode} finished ${formatDateTime(run.finishedAt)} · ${formatNumber(records)} records read`,
      live: false,
      fraction,
    }
  // The three phases that are not just "reading a module". Partial on purpose:
  // every other phase falls through to the name of what it is reading.
  const phase = PHASE_PHRASE[run.phase] ?? `reading ${run.phase}`
  return {
    label: `${mode} ${run.status}: ${phase} · ${formatNumber(records)} records so far`,
    live,
    fraction,
  }
}

function Field({ label, value }: Readonly<{ label: string; value: string }>): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

/** The org's name and id, when the connection reports both. */
function orgLabel(orgName: string | null | undefined, orgId: string | null | undefined): string {
  const id = orgId ? ` · ${orgId}` : ''
  return `${orgName ?? EM_DASH}${id}`
}

const PHASE_PHRASE: Record<string, string> = {
  resolve: 'deriving domains',
  resolve_by_name: 'resolving names',
  finish: 'finishing',
}

type ChipTone = 'default' | 'success' | 'warning' | 'error' | 'info'

/**
 * How each connection state reads on the chip.
 *
 * A table rather than a five-deep ternary (Sonar S3358): a `Record` over the
 * union is checked for exhaustiveness, so adding a state to `ConnectionState`
 * is a compile error here rather than a silent fall-through to "Not connected".
 */
const STATE_CHIP: Record<ConnectionState, readonly [string, ChipTone]> = {
  connected: ['Connected', 'success'],
  paused: ['Paused', 'warning'],
  connecting: ['Connecting', 'info'],
  error: ['Needs attention', 'error'],
  not_deployed: ['Not deployed', 'default'],
  disconnected: ['Not connected', 'default'],
}

function StatusChip({ state }: Readonly<{ state: ConnectionState }>): JSX.Element {
  const [label, color] = STATE_CHIP[state]
  return (
    <Chip size="small" color={color} label={label} variant={color === 'default' ? 'outlined' : 'filled'} />
  )
}

/**
 * Web-form spam the sync held back. Nothing is deleted from Zoho automatically:
 * each links to its record there, and a person deletes it.
 */
function SpamHeldBack({ crmUrlBase }: Readonly<{ crmUrlBase: string | null }>): JSX.Element | null {
  const spam = useZohoSpam(true)
  const [open, setOpen] = useState(false)
  const items = spam.data?.items ?? []
  if (spam.isLoading || items.length === 0) return null
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>
          Spam held back ({formatNumber(items.length)})
        </Typography>
        <Button size="small" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Records that look like web-form spam — random-string names, text-message gateway or cut-up Gmail
        addresses, test entries — are not imported. Delete them in Zoho; they leave this list when Zoho
        reports them deleted.
      </Typography>
      {open ? (
        <Box sx={{ mt: 1, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Why</TableCell>
                <TableCell>Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.zohoKey}>
                  <TableCell>
                    {crmUrlBase ? (
                      <Link
                        href={`${crmUrlBase}/tab/${item.module}/${item.zohoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {item.name || item.zohoId}
                      </Link>
                    ) : (
                      item.name || item.zohoId
                    )}
                  </TableCell>
                  <TableCell>{item.company ?? EM_DASH}</TableCell>
                  <TableCell>{item.reasons.join(', ')}</TableCell>
                  <TableCell>{formatDateTime(item.at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Box>
  )
}

function ReportTable({ run }: Readonly<{ run: ZohoImportRun }>): JSX.Element {
  const r = run.report
  const people = (['contacts', 'leads'] as const).map((k) => ({ key: k, ...r[k] }))
  const domainFrom = Object.entries(r.accounts.domainFrom)
  return (
    <Stack spacing={1.5}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Accounts</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">Already profiled</TableCell>
            <TableCell align="right">New companies</TableCell>
            <TableCell align="right">No domain</TableCell>
            <TableCell align="right">Unchanged</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{run.mode === 'dry_run' ? 'Would be' : 'Were'}</TableCell>
            <TableCell align="right">{formatNumber(r.accounts.total)}</TableCell>
            <TableCell align="right">{formatNumber(r.accounts.matchedExistingCompany)}</TableCell>
            <TableCell align="right">{formatNumber(r.accounts.newCompany)}</TableCell>
            <TableCell align="right">{formatNumber(r.accounts.noDomain)}</TableCell>
            <TableCell align="right">{formatNumber(r.accounts.unchanged)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {domainFrom.length > 0 || r.accounts.resolveByNameNeeded > 0 ? (
        <Typography variant="caption" color="text.secondary">
          Domains derived:{' '}
          {domainFrom
            .map(([basis, n]) => `${formatNumber(n)} from ${basis.replaceAll('_', ' ')}`)
            .join(', ') || 'none'}
          {r.accounts.resolveByNameNeeded > 0
            ? ` · ${formatNumber(r.accounts.resolveByNameNeeded)} would need the paid name lookup`
            : ''}
          {r.accounts.resolveByNameCredits > 0
            ? ` · ${formatNumber(r.accounts.resolveByNameCredits)} credits spent on it`
            : ''}
        </Typography>
      ) : null}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>People</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">With email</TableCell>
            <TableCell align="right">With phone</TableCell>
            <TableCell align="right">With LinkedIn</TableCell>
            <TableCell align="right">Already in Quarry</TableCell>
            <TableCell align="right">Duplicate email</TableCell>
            <TableCell align="right">Opted out</TableCell>
            <TableCell align="right">Spam</TableCell>
            <TableCell align="right">Created</TableCell>
            <TableCell align="right">Updated</TableCell>
            <TableCell align="right">Unchanged</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {people.map((p) => (
            <TableRow key={p.key}>
              <TableCell sx={{ textTransform: 'capitalize' }}>
                {p.key}
                {p.key === 'leads' && p.converted > 0 ? ` (${formatNumber(p.converted)} converted)` : ''}
              </TableCell>
              <TableCell align="right">{formatNumber(p.total)}</TableCell>
              <TableCell align="right">{formatNumber(p.withEmail)}</TableCell>
              <TableCell align="right">{formatNumber(p.withMobile)}</TableCell>
              <TableCell align="right">{formatNumber(p.withLinkedin)}</TableCell>
              <TableCell align="right">{formatNumber(p.matchedQuarryContact)}</TableCell>
              <TableCell align="right">{formatNumber(p.duplicateEmailInCrm)}</TableCell>
              <TableCell align="right">{formatNumber(p.optOut)}</TableCell>
              <TableCell align="right">{formatNumber(p.spam ?? 0)}</TableCell>
              <TableCell align="right">{formatNumber(p.created)}</TableCell>
              <TableCell align="right">{formatNumber(p.updated)}</TableCell>
              <TableCell align="right">{formatNumber(p.unchanged)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {r.samples.duplicateEmails.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {formatNumber(r.samples.duplicateEmails.length)} addresses appear on more than one CRM record; each
          was linked to one person here.{' '}
          {r.samples.conflicts > 0
            ? `${formatNumber(r.samples.conflicts)} values disagreed with what a provider had found and were kept as conflicts.`
            : ''}
        </Typography>
      ) : null}
    </Stack>
  )
}

export function ZohoCrmSection(): JSX.Element {
  const api = useApi()
  const queryClient = useQueryClient()
  const [requestedAt, setRequestedAt] = useState<number | null>(null)
  const [dc, setDc] = useState<ZohoDc>('in')
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [confirmApply, setConfirmApply] = useState(false)
  const [modules, setModules] = useState<ZohoModule[]>([...ZOHO_MODULES])
  const [resolveByName, setResolveByName] = useState(false)
  const [creditBudget, setCreditBudget] = useState(200)

  const status = useZohoStatus(true, false)
  const state = connectionState(status.data, requestedAt)
  const latest = status.data?.latestImport ?? null
  const importLive = latest !== null && (latest.status === 'queued' || latest.status === 'running')
  const live = state === 'connecting' || importLive
  const polled = useZohoStatus(live, live)
  const data = polled.data ?? status.data

  useEffect(() => {
    if (data?.settings.dc) setDc(data.settings.dc)
  }, [data?.settings.dc])

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: qk.zohoStatus })
    await queryClient.invalidateQueries({ queryKey: qk.me })
  }

  const connect = useMutation({
    mutationFn: () => api.zohoConnect(dc),
    onSuccess: async () => {
      setRequestedAt(Date.now())
      await refresh()
    },
  })
  const disconnect = useMutation({
    mutationFn: () => api.zohoDisconnect(),
    onSuccess: async () => {
      setConfirmDisconnect(false)
      setRequestedAt(null)
      await refresh()
    },
  })
  const save = useMutation({
    mutationFn: (patch: ZohoSettingsPatch) => api.updateZohoSettings(patch),
    onSuccess: refresh,
  })
  const startImport = useMutation({
    mutationFn: (mode: 'dry_run' | 'apply') =>
      api.startZohoImport({
        mode,
        modules,
        ...(resolveByName ? { resolveByName: { enabled: true, creditBudget } } : {}),
      }),
    onSuccess: async () => {
      setConfirmApply(false)
      await refresh()
    },
  })

  const summary = useMemo(() => importSummary(latest), [latest])

  if (status.isLoading) return <LoadingPanel label="Loading the Zoho CRM block" />
  if (status.isError) return <ErrorPanel error={status.error} onRetry={() => void status.refetch()} />
  if (!data) return <LoadingPanel label="Loading the Zoho CRM block" />

  const z: ZohoSettings = data.settings
  const missing = missingFieldLines(z.missingFields)
  const creditShare =
    data.dailyCap > 0 ? Math.min(100, Math.round((data.creditsToday / data.dailyCap) * 100)) : 0
  const busy = connect.isPending || disconnect.isPending || save.isPending

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Zoho CRM</Typography>
          <StatusChip state={state} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Accepted people reach the CRM as Contacts under their Accounts; edits, opt-outs and deletions in the
          CRM come back here. Importing the CRM brings every Account, Contact and Lead in, so nobody the CRM
          already holds is bought again.
        </Typography>

        {state === 'not_deployed' ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>This deployment has no Zoho queues yet</AlertTitle>
            The sync needs the CRM tables, queues and workers deployed. Until then nothing here can be
            connected.
          </Alert>
        ) : null}

        {z.lastError && (state === 'error' || state === 'connected' || state === 'paused') ? (
          <Alert severity={state === 'error' ? 'error' : 'warning'} sx={{ mt: 2 }}>
            <AlertTitle>{state === 'error' ? 'The last attempt failed' : 'The last error'}</AlertTitle>
            {z.lastError}
            {z.lastErrorAt ? ` (${formatDateTime(z.lastErrorAt)})` : ''}
          </Alert>
        ) : null}

        {!data.connected && state !== 'not_deployed' ? (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2">
              Before connecting: create the API client and put its three values in Parameter Store (the
              bootstrap script names them), give the integration user a licence, and create the Quarry fields
              in the CRM — the handshake lists any that are missing.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="zoho-dc-label">Data centre</InputLabel>
                <Select
                  labelId="zoho-dc-label"
                  label="Data centre"
                  value={dc}
                  onChange={(event) => setDc(event.target.value as ZohoDc)}
                  disabled={busy || state === 'connecting'}
                >
                  {ZOHO_DCS.map((code) => (
                    <MenuItem key={code} value={code}>
                      {DC_LABEL[code]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={busy || state === 'connecting'}
                onClick={() => connect.mutate()}
              >
                {state === 'connecting' ? 'Connecting' : 'Connect'}
              </Button>
            </Stack>
            {state === 'connecting' ? (
              <Box>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">
                  Reading the org, the users and the fields, and registering the notification channel.
                </Typography>
              </Box>
            ) : null}
            {connect.isError ? <ErrorPanel error={connect.error} /> : null}
          </Stack>
        ) : null}

        {data.connected ? (
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Org" value={orgLabel(z.orgName, z.orgId)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field
                  label="Integration user"
                  value={z.integrationUserEmail ?? z.integrationUserName ?? EM_DASH}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Connected" value={formatDateTime(z.connectedAt)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field
                  label="Notification channel"
                  value={
                    z.channelExpiresAt ? `Expires ${formatDateTime(z.channelExpiresAt)}` : 'Not registered'
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Last push" value={formatDateTime(z.lastPushAt)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Last pull" value={formatDateTime(z.lastPullAt)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field label="Last reconcile" value={formatDateTime(z.lastReconcileAt)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Zoho API credits today
                  </Typography>
                  <Typography variant="body2">
                    {formatNumber(data.creditsToday)} of {formatNumber(data.dailyCap)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={creditShare}
                    color={creditShare > 80 ? 'warning' : 'primary'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Grid>
            </Grid>

            {missing.length > 0 ? (
              <Alert severity="warning">
                <AlertTitle>Fields to create in Zoho CRM before pushing</AlertTitle>
                <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                  {missing.map((line) => (
                    <Typography component="li" variant="body2" key={line.module}>
                      <strong>{line.module}</strong>: {line.fields.join(', ')}
                    </Typography>
                  ))}
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Setup → Customization → Modules and Fields. Then re-verify.
                </Typography>
              </Alert>
            ) : null}

            <Divider />

            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="zoho-push-label">Push mode</InputLabel>
                  <Select
                    labelId="zoho-push-label"
                    label="Push mode"
                    value={z.pushMode}
                    disabled={busy}
                    onChange={(event) =>
                      save.mutate({ pushMode: event.target.value as ZohoSettings['pushMode'] })
                    }
                  >
                    <MenuItem value="auto">Automatic</MenuItem>
                    <MenuItem value="manual">Manual (Send to Zoho)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="zoho-owner-label">Default owner</InputLabel>
                  <Select
                    labelId="zoho-owner-label"
                    label="Default owner"
                    value={z.defaultOwnerId ?? ''}
                    disabled={busy}
                    onChange={(event) =>
                      save.mutate({ defaultOwnerId: event.target.value ? String(event.target.value) : null })
                    }
                  >
                    <MenuItem value="">The integration user</MenuItem>
                    {z.users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={z.paused}
                      disabled={busy}
                      onChange={(event) => save.mutate({ paused: event.target.checked })}
                    />
                  }
                  label="Pause pushes"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={z.triggerWorkflows}
                      disabled={busy}
                      onChange={(event) => save.mutate({ triggerWorkflows: event.target.checked })}
                    />
                  }
                  label="Fire CRM workflows on push"
                />
              </Grid>
            </Grid>
            {save.isError ? <ErrorPanel error={save.error} /> : null}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                disabled={busy || state === 'connecting'}
                onClick={() => connect.mutate()}
              >
                {state === 'connecting' ? 'Re-verifying' : 'Re-verify'}
              </Button>
              <Button color="error" disabled={busy} onClick={() => setConfirmDisconnect(true)}>
                Disconnect
              </Button>
            </Stack>

            <SpamHeldBack crmUrlBase={data.crmUrlBase} />

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Import the CRM
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A dry run reads everything and reports what would change without writing. Apply brings every
                Account, Contact and Lead into Quarry under a job called “Zoho CRM import”; running it again
                only touches records that changed in the CRM since.
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                {ZOHO_MODULES.map((module) => (
                  <FormControlLabel
                    key={module}
                    control={
                      <Checkbox
                        checked={modules.includes(module)}
                        disabled={importLive}
                        onChange={(event) =>
                          setModules((current) =>
                            event.target.checked
                              ? [...new Set([...current, module])]
                              : current.filter((m) => m !== module),
                          )
                        }
                      />
                    }
                    label={module}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Switch
                      checked={resolveByName}
                      disabled={importLive}
                      onChange={(event) => setResolveByName(event.target.checked)}
                    />
                  }
                  label="Look up domain-less Accounts by name (paid)"
                />
                {resolveByName ? (
                  <TextField
                    size="small"
                    type="number"
                    label="Credit budget"
                    value={creditBudget}
                    disabled={importLive}
                    onChange={(event) =>
                      setCreditBudget(Math.max(0, Math.min(100_000, Number(event.target.value) || 0)))
                    }
                    sx={{ width: 140 }}
                  />
                ) : null}
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                <Button
                  variant="outlined"
                  disabled={importLive || startImport.isPending || modules.length === 0}
                  onClick={() => startImport.mutate('dry_run')}
                >
                  Dry run
                </Button>
                <Button
                  variant="contained"
                  disabled={importLive || startImport.isPending || modules.length === 0}
                  onClick={() => setConfirmApply(true)}
                >
                  Apply import
                </Button>
              </Stack>
              {startImport.isError ? <ErrorPanel error={startImport.error} /> : null}

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">{summary.label}</Typography>
                {summary.live ? (
                  <LinearProgress
                    variant={summary.fraction === null ? 'indeterminate' : 'determinate'}
                    value={(summary.fraction ?? 0) * 100}
                    sx={{ mt: 0.5 }}
                  />
                ) : null}
                {latest ? (
                  <Typography variant="caption" color="text.secondary">
                    Run {latest.runId} · started {formatDateTime(latest.startedAt)} by {latest.startedBy} ·{' '}
                    {latest.modules.join(', ')}
                  </Typography>
                ) : null}
              </Box>
              {latest && latest.status !== 'queued' ? (
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                  <ReportTable run={latest} />
                </Box>
              ) : null}
              {latest?.status === 'done' && latest.mode === 'apply' ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Link href="/contacts?gate=accepted&crm=zoho">The people it brought in</Link> are on the
                  Contacts screen under “In Zoho CRM”.
                </Typography>
              ) : null}
            </Box>
          </Stack>
        ) : null}

        <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)} fullWidth maxWidth="sm">
          <DialogTitle>Disconnect Zoho CRM?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Pushes stop, the notification channel is removed, and nothing more comes back from the CRM. What
              was already imported stays here, still marked as held by the CRM. Reconnecting later resumes
              from where the reconcile walk left off.
            </Typography>
            {disconnect.isError ? <ErrorPanel error={disconnect.error} /> : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              Disconnect
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmApply} onClose={() => setConfirmApply(false)} fullWidth maxWidth="sm">
          <DialogTitle>Import {modules.join(', ')} from Zoho CRM?</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Every record is written into Quarry: companies under their domains, people under the job “Zoho
                CRM import”, with the CRM’s emails and phones as their source. People the CRM holds are never
                bought from a provider again.
              </Typography>
              {resolveByName ? (
                <Typography variant="body2">
                  Accounts with no website and no address to derive one from will be looked up by name with
                  the active provider, up to {formatNumber(creditBudget)} credits.
                </Typography>
              ) : null}
              {startImport.isError ? <ErrorPanel error={startImport.error} /> : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmApply(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={startImport.isPending}
              onClick={() => startImport.mutate('apply')}
            >
              {startImport.isPending ? 'Starting' : 'Import'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  )
}
